import { allmostGraphModules as allmostModules, allmostGraphDescriptors as allmostDescriptors } from './audio-engine.js';
import { hostDefinitions } from './live-graph-modules.js';
const graphDescriptors=[...allmostDescriptors,...hostDefinitions.map(d=>d.descriptor)];
import { ModuleRegistry, PatchRuntime } from '../../packages/module-runtime/src/index.js';
import { ModulationMatrix, mod017Descriptor } from '../../packages/mod-017/src/index.js';
import { defineDspModule } from '../../packages/dsp-module-contract/src/index.js';
import type { PatchEndpoint, PatchGraphV1 } from '../../packages/routing-graph/src/index.js';

/** Explicit one-input identity route through the real MOD017 matrix.
 * Rich multi-route matrices still use their standalone host configuration API.
 * No invented MIDI offset is applied to persisted graph cables.
 */
const matrixBridge=defineDspModule({descriptor:mod017Descriptor,createInstance:({sampleRate})=>{
 const matrix=new ModulationMatrix({sampleRate,sources:[{id:'in',kind:'control'}],targets:[{id:'out',kind:'parameter',minimum:-1,maximum:1,baseValue:0}],routes:[{id:'identity',source:'in',target:'out',depth:1,transform:'identity'}]});
 return {reset(){matrix.reset();},setParameter(){throw new RangeError('MOD017 bridge has no scalar parameters');},process(b){
 const input=b.controls.in?.subarray(0,b.frames)??new Float32Array(b.frames);
 matrix.processInto({in:input},{out:b.outputs.out!.subarray(0,b.frames)});
 }};
}});
export const graphAudioOutputs=(graph:PatchGraphV1):PatchEndpoint[]=>graph.nodes.flatMap(node=>
 (graphDescriptors.find(d=>d.id===node.moduleId)?.outputs??[]).filter(p=>p.kind==='audio'&&p.channels===1).map(p=>({nodeId:node.id,portId:p.id})));

/** Opt-in bounded DAG snapshot, not live synthesis and not the legacy fixed host.
 * Asset engines with no loaded asset stay silent/dry according to their own contract.
 * Rendering accepts one mono port tap; no implicit sum, feedback or asset acquisition.
 */
export function renderEditedGraph(graph:PatchGraphV1,output:PatchEndpoint,sampleRate=48000,frames=96000) {
 if(!Number.isFinite(sampleRate)||sampleRate<8000||sampleRate>192000)throw new RangeError('sampleRate outside supported range');
 if(!Number.isInteger(frames)||frames<1||frames>sampleRate*10)throw new RangeError('frames outside 1..10 seconds');
 if(graph.nodes.length>64||graph.connections.length>256)throw new RangeError('graph resource limit exceeded');
 if(!graphAudioOutputs(graph).some(p=>p.nodeId===output.nodeId&&p.portId===output.portId))throw new RangeError('select an audio output');
 if(graph.connections.some(c=>c.feedback))throw new RangeError('feedback graph is not supported by this DAG renderer');
 const runtime=new PatchRuntime(graph,new ModuleRegistry([...allmostModules,matrixBridge,...hostDefinitions]),{sampleRate,maxBlockSize:128});
 const samples=new Float32Array(frames);let peak=0,energy=0,limitedSamples=0;
 for(let at=0;at<frames;at+=128){const count=Math.min(128,frames-at),buffer=runtime.process(count,output);
  for(let i=0;i<count;i++) {const raw=buffer[i]!;if(!Number.isFinite(raw))throw new RangeError('module produced non-finite audio');
   // Audition safety is host-only, never attributed to a package algorithm.
   const value=Math.max(-.95,Math.min(.95,raw*.25));if(value!==raw*.25)limitedSamples++;
   samples[at+i]=value;peak=Math.max(peak,Math.abs(value));energy+=value*value;
  }
 }
 return {samples,peak,rms:Math.sqrt(energy/frames),limitedSamples,output:{...output},frames,sampleRate};
}
